// EditBox.component.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDataLayer } from "../../../Contexts/DataLayer.context.jsx";

/**
 * EditBox – standalone editor for a List.
 * Props:
 *   - listID: string | number  (the list to edit)
 *
 * Pulls listArray & updateListArray from DataLayer context.
 * No other props are required.
 */
const EditBox = ({ listID, onClose }) => {

  const { listArray, updateListArray } = useDataLayer();

  // derive + local editable copy
  const recordFromContext = useMemo(
    () => listArray?.find((i) => String(i.listId) === String(listID)) || null,
    [listArray, listID]
  );

  // local state mirrors the record so edits are controlled
  const [theRecordToEdit, setTheRecordToEdit] = useState(recordFromContext);
  const [showConfirmationBox, setShowConfirmationBox] = useState(false);
  const [showDeleteConfirmationBox, setShowDeleteConfirmationBox] = useState(false);

  // Special lists cannot be renamed/deleted
  const isSpecial = useMemo(() => {
    const name = theRecordToEdit?.listName ?? "";
    return name === "-QUICK LIST-" || name === "-WATCHLIST-";
  }, [theRecordToEdit]);

  // keep local state in sync if listArray/listID changes externally
  useEffect(() => {
    setTheRecordToEdit(recordFromContext);
  }, [recordFromContext]);

  // --- Add inside EditBox.component.jsx (top-level of the component function) ---

// 1) rename helper (same endpoint you used before)
const fireAndForgetRename = (listId, newName) => {
  fetch('https://www.fbxchange.com/ReactPHP/listBox.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      method: 'renameList',
      listID: String(listId).trim(),
      listName: newName ?? '',
    }).toString(),
  }).catch(err => console.error('rename failed:', err));
};

// 2) Save list name (was letsChangeTheInfo in LittleBox)
const letsChangeTheInfo = () => {
  if (!theRecordToEdit) return;

  const original = listArray.find(i => String(i.listId) === String(theRecordToEdit.listId));
  const nameChanged = (theRecordToEdit.listName ?? "") !== (original?.listName ?? "");

  // update local context copy
  const updated = listArray.map((item) =>
    String(item.listId) === String(theRecordToEdit.listId)
      ? { ...item, ...theRecordToEdit }
      : item
  );

  if (nameChanged) {
    fireAndForgetRename(theRecordToEdit.listId, theRecordToEdit.listName);
  }

  updateListArray(updated);
if (typeof onClose === 'function') onClose();

};

// 3) Remove all players from this list
const removePlayers = async () => {
  if (!theRecordToEdit) return;

  const draft = { ...theRecordToEdit, theNumbers: [], listTotal: 0 };

  // optimistic UI update
  updateListArray(
    listArray.map(item => (String(item.listId) === String(draft.listId) ? draft : item))
  );
  if (typeof onClose === 'function') onClose();
  // server call
  const cleanId = String(draft.listId).trim();
  const res = await fetch('https://www.fbxchange.com/ReactPHP/tomBox.php', {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ script: 'removeAllPlayers2025', listID: cleanId }),
  }).catch(() => null);

  if (res && res.ok) {
    try { await res.json(); } catch { /* ignore */ }
  }

  // close any local confirmation UI
  setShowConfirmationBox(false);
};

// 4) Delete this list
const deleteList = async () => {
  if (!theRecordToEdit) return;

  const id = theRecordToEdit.listId;

  // optimistic UI update
  updateListArray(listArray.filter(item => String(item.listId) !== String(id)));
  if (typeof onClose === 'function') onClose();

  // server call
  const cleanId = String(id).trim();
  const res = await fetch('https://www.fbxchange.com/ReactPHP/tomBox.php', {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ script: 'removeList2025', listID: cleanId }),
  }).catch(() => null);

  if (res && res.ok) {
    try { await res.json(); } catch { /* ignore */ }
  }

  setShowDeleteConfirmationBox(false);
};


  // If list not found, show nothing (or a gentle notice)
  if (!theRecordToEdit) {
    return (
      <div className="alert alert-warning m-0">
        List not found for ID: <strong>{String(listID)}</strong>
      </div>
    );
  }

  return (
    <div
      id="editContent"
      style={{
        display: "block",
        height: "100%",
      }}
    >
      <div className="instruction-panel">
        {/* LEFT SIDE (instructions) */}
        <div className="left alert-navy">
          <div className="fw-bold fs-6 text-center">Instructions</div>

          <div className="mt-2 text-start fw-bold">Change List Name</div>
          <p className="mt-1">
            In the box on the right, change the name of the list and press the "Update" button under the name.
          </p>

          <div className="mt-2 text-start fw-bold">Remove All Players</div>
          <p className="mt-1">
            To remove all players from a list, But Keep the List press the "Remove All Players" button.
          </p>

          <div className="mt-2 text-start fw-bold">Delete the List (and Players)</div>
          <p className="mt-1">
            To remove all players from a list, and Delete the Actual List, Press the "Delete This List" Button.
          </p>
        </div>

        {/* RIGHT SIDE (form/actions) */}
        <div className="right p-3">
          <div className="fw-bold fs-6 text-center mb-3">Information</div>

          {/* Rename */}
          {isSpecial ? (
            <div className="alert alert-warning text-center">
              The List <span className="fw-bold text-danger">{theRecordToEdit.listName}</span> may not be modified or
              deleted because it is a custom list created by FBX for you.{" "}
              <span className="fw-bold">You can still use this area to remove all records from the List</span>.
            </div>
          ) : (
            <div id="boxForListChange">
              <div className="row align-items-center mb-3">
                <div className="col-48">
                  <input
                    type="text"
                    className="form-control mb-2 p-2"
                    placeholder="Enter new list name"
                    aria-label="Change List Name"
                    value={theRecordToEdit.listName || ""}
                    onChange={(e) =>
                      setTheRecordToEdit((prev) => ({ ...(prev || {}), listName: e.target.value }))
                    }
                  />
                </div>
                <div className="col-48 text-center">
                  <button className="btn btn-primary btn-sm w-50 fs-7" onClick={letsChangeTheInfo}>
                    Update List Name
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Players count + Remove All */}
          {!showConfirmationBox && (
            <div className="row align-items-center mb-3">
              <div className="col-48">
                <div className="text-start fw-bold text-center">{theRecordToEdit.listTotal} Players on List</div>
              </div>
              <div className="col-48 mt-2 text-center">
                <button
                  className="btn btn-warning btn-sm w-50 fs-7"
                  onClick={() => setShowConfirmationBox(true)}
                >
                  Remove All Players
                </button>
              </div>
            </div>
          )}

          {/* Remove All confirm */}
          {showConfirmationBox && (
            <div className="row mt-3">
              <div className="col-48 alert alert-success p-3 text-center">
                <p className="fw-bold mb-3">Are you sure you want to remove all players?</p>
                <div className="d-flex gap-2 justify-content-around">
                  <button
                    className="btn btn-success text-white btn-sm w-40"
                    onClick={() => {
                      removePlayers();
                      setShowConfirmationBox(false);
                    }}
                  >
                    Yes, Remove Players
                  </button>
                  <button
                    className="btn btn-danger btn-sm w-40"
                    onClick={() => setShowConfirmationBox(false)}
                  >
                    Do Not Remove Players
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete list */}
          {!isSpecial && (
            <>
              {!showDeleteConfirmationBox && (
                <div className="row mt-2">
                  <div className="col-48">
                    <div className="text-start fw-bold text-center">No Longer Want This List?</div>
                    <p className="text-center fs-8 text-danger">
                      This Removes the List and All Players on that List
                    </p>
                  </div>
                  <div className="col-48 text-center mt-1">
                    <button
                      className="btn btn-danger btn-sm w-50 fs-7"
                      onClick={() => setShowDeleteConfirmationBox(true)}
                    >
                      Delete This List
                    </button>
                  </div>
                </div>
              )}

              {showDeleteConfirmationBox && (
                <div className="row mt-3">
                  <div className="col-48 alert alert-danger p-3 text-center">
                    <p className="fw-bold mb-3">Are you sure you want to delete this list?</p>
                    <div className="d-flex gap-2 justify-content-around">
                      <button
                        className="btn btn-success text-white btn-sm w-40"
                        onClick={() => {
                          deleteList();
                          setShowDeleteConfirmationBox(false);
                        }}
                      >
                        Yes, Delete List
                      </button>
                      <button
                        className="btn btn-danger btn-sm w-40"
                        onClick={() => setShowDeleteConfirmationBox(false)}
                      >
                        No, Do Not Delete List
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer area: keep for parity; no external onClose here */}
        <div
          className="text-center"
          style={{ position: "absolute", bottom: "10px", left: "50%", transform: "translateX(-50%)" }}
        >
        </div>
      </div>
    </div>
  );
};

export default EditBox;
